import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Hook to track the active section based on scroll and URL changes.
 * @param {Array<{ link: string }>} links - Navigation links with potential anchor targets.
 * @returns {{ activeSection: string, isActiveLink: (link: string) => boolean }}
 *
 *  - Updates `activeSection` when scrolling past observed sections.
 *  - Resets on pathname change.
 *  - `isActiveLink` checks if a link is currently active.
 */

export function useActiveLink(links: { link: string }[]): {
	activeSection: string
	isActiveLink: (link: string) => boolean
} {
	const [activeSection, setActiveSection] = useState('')
	const location = useLocation()

	useEffect(() => {
		setActiveSection('')
	}, [location.pathname])

	useEffect(() => {
		const sections = links.flatMap(link => {
			const sectionId = link.link.split('#')[1]
			const section = sectionId ? document.getElementById(sectionId) : null
			return section ? [section] : []
		})

		if (!sections.length) return

		const observer = new IntersectionObserver(
			entries => {
				entries.forEach(entry => {
					if (entry.isIntersecting) {
						setActiveSection(entry.target.id)
						window.history.replaceState({}, '', `${location.pathname}#${entry.target.id}`)
					} else {
						setActiveSection('')
						window.history.replaceState({}, '', location.pathname)
					}
				})
			},
			{ rootMargin: '-30% 0px', threshold: 0.1 }
		)

		sections.forEach(section => observer.observe(section))

		return () => observer.disconnect()
	}, [links, location.pathname])

	const isActiveLink = (link: string) => {
		const sectionId = link.split('#')[1]
		if (activeSection !== '') return activeSection === sectionId
		return location.pathname === link
	}

	return { activeSection, isActiveLink }
}
